/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
/**
 * React hook for managing application state in a client-held architecture.
 *
 * The client is responsible for:
 * - Storing the complete app_state in React state
 * - Sending the current app_state with every mutating API request
 * - Updating local state with the app_state returned from the server
 *
 * The server does not persist state - it only processes requests and returns
 * updated state, making it fully stateless and horizontally scalable.
 */
import { useCallback, useState } from "react";
import { apiService } from "../api";
import logger from "../logger";
export const useAppState = () => {
    const [sessionId, setSessionId] = useState(null);
    const [appState, setAppState] = useState(null);
    const [scoreboardData, setScoreboardData] = useState(null);
    const [ideasChartData, setIdeasChartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const updateFromResponse = useCallback((response) => {
        setSessionId(response.session_id);
        setAppState(response.app_state);
        setScoreboardData(response.scoreboard_data);
        setIdeasChartData(response.ideas_chart_data);
    }, []);
    // Log the error to the console and set a user-facing message.
    const logAndSetError = useCallback((contextMessage, err) => {
        logger.error("%o", err);
        const detail = err instanceof Error ? err.message : String(err ?? "Unknown error");
        setError(`${contextMessage}: ${detail}`);
    }, []);
    /**
     * Helper to handle streaming with fallback to batch mode.
     * Reduces duplication between generateAIIdeas and calibrateSession.
     */
    const handleStreamingWithFallback = useCallback(async (streamingFn, fallbackFn, fallbackErrorMsg) => {
        let serverErrorOccurred = false;
        try {
            await streamingFn((response) => {
                if (response.success) {
                    setAppState(response.app_state);
                    setScoreboardData(response.scoreboard_data);
                    setIdeasChartData(response.ideas_chart_data);
                }
                else {
                    // response is ApiErrorResponse
                    const serverErr = "error" in response ? response.error : "Unknown error";
                    logAndSetError(fallbackErrorMsg, serverErr);
                    serverErrorOccurred = true;
                }
            });
        }
        catch (streamError) {
            // If server already sent an SSE error, don't fall back — the server has already evaluated and rejected
            // the request, so retrying via `fallbackFn` will likely reproduce the same error or cause duplicate
            // side-effects.
            if (serverErrorOccurred) {
                return;
            }
            logger.warn("Streaming failed, falling back to batch mode: %o", streamError);
            // Fallback to non-streaming mode for network/protocol errors
            try {
                const response = await fallbackFn();
                if (response.success) {
                    setAppState(response.app_state);
                    setScoreboardData(response.scoreboard_data);
                    setIdeasChartData(response.ideas_chart_data);
                }
                else {
                    const errorMsg = response.error || "Unknown error";
                    logAndSetError(fallbackErrorMsg, errorMsg);
                }
            }
            catch (fallbackError) {
                logAndSetError(fallbackErrorMsg, fallbackError);
            }
        }
        // Stream cleanup (reader.cancel) is handled in the API layer's finally block
    }, [setAppState, setScoreboardData, setIdeasChartData, setError, logAndSetError]);
    const initializeSession = useCallback(async (data) => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.initializeSession(data);
            updateFromResponse(response);
        }
        catch (err) {
            logAndSetError("Error initializing session", err);
        }
        finally {
            setLoading(false);
        }
    }, [updateFromResponse, logAndSetError]);
    const submitHumanIdea = useCallback(async (idea) => {
        if (!sessionId) {
            setError("No active session");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.submitIdea({
                session_id: sessionId,
                idea,
                app_state: appState || undefined, // Send current state for stateless operation
            });
            if (response.success) {
                setAppState(response.app_state);
                setScoreboardData(response.scoreboard_data);
                setIdeasChartData(response.ideas_chart_data);
            }
            else {
                // Defensive: API should throw on errors, but handle just in case
                const errorMsg = "error" in response ? String(response.error) : "Unknown error";
                logAndSetError("Error submitting idea", errorMsg);
            }
        }
        catch (err) {
            logAndSetError("Error submitting idea", err);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, appState, logAndSetError]);
    const generateAIIdea = useCallback(async (mode = "normal") => {
        if (!sessionId) {
            setError("No active session");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.generateIdea({
                session_id: sessionId,
                mode,
                app_state: appState || undefined, // Send current state for stateless operation
            });
            if (response.success) {
                setAppState(response.app_state);
                setScoreboardData(response.scoreboard_data);
                setIdeasChartData(response.ideas_chart_data);
            }
            else {
                // Defensive: API should throw on errors, but handle just in case
                const errorMsg = "error" in response ? String(response.error) : "Unknown error";
                logAndSetError("Error generating idea", errorMsg);
            }
        }
        catch (err) {
            logAndSetError("Error generating idea", err);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, appState, logAndSetError]);
    const generateAIIdeas = useCallback(async (nIdeas = 10) => {
        if (!sessionId) {
            setError("No active session");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            await handleStreamingWithFallback((onUpdate) => apiService.generateIdeasStreaming({
                session_id: sessionId,
                n_ideas: nIdeas,
                app_state: appState || undefined,
            }, onUpdate), () => apiService.generateIdeas({
                session_id: sessionId,
                n_ideas: nIdeas,
                app_state: appState || undefined,
            }), "Error generating ideas");
        }
        catch (err) {
            logAndSetError("Error generating ideas", err);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, appState, handleStreamingWithFallback, logAndSetError]);
    const calibrateSession = useCallback(async () => {
        if (!sessionId) {
            setError("No active session");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            await handleStreamingWithFallback((onUpdate) => apiService.calibrateSessionStreaming(sessionId, appState || undefined, onUpdate), () => apiService.calibrateSession(sessionId, appState || undefined), "Error calibrating session");
        }
        catch (err) {
            logAndSetError("Error calibrating session", err);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, appState, handleStreamingWithFallback, logAndSetError]);
    const updateCheckbox = useCallback(async (label, value) => {
        if (!sessionId || !appState) {
            setError("No active session");
            return;
        }
        // Optimistically update local state
        const newCheckboxStates = { ...appState.checkbox, [label]: value };
        setAppState((prev) => (prev ? { ...prev, checkbox: newCheckboxStates } : prev));
    }, [sessionId, appState, logAndSetError]);
    const updateSuggestionsValue = useCallback((value) => {
        setAppState((prev) => (prev ? { ...prev, suggestions_value: value } : prev));
    }, []);
    const downloadIdeas = useCallback(async () => {
        if (!sessionId) {
            setError("No active session");
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await apiService.downloadIdeas(sessionId, appState);
            // Create and trigger download
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = response.filename;
            document.body.append(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (err) {
            logAndSetError("Error downloading ideas", err);
        }
        finally {
            setLoading(false);
        }
    }, [sessionId, appState, logAndSetError]);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    return {
        sessionId,
        appState,
        scoreboardData,
        ideasChartData,
        loading,
        error,
        initializeSession,
        submitHumanIdea,
        generateAIIdea,
        generateAIIdeas,
        calibrateSession,
        updateCheckbox,
        updateSuggestionsValue,
        downloadIdeas,
        clearError,
    };
};
